const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app';

export interface ApiResponse<T = any> {
  data?: T;
  message?: string;
  success: boolean;
}

export interface UserRoleResponse {
  role: 'volunteer' | 'admin';
  is_admin: boolean;
  volunteer_id?: number;
  email: string;
}

export interface VolunteerData {
  id: string;
  firebase_uid: string;
  first_name: string;
  last_name: string;
  email: string;
}

// Application-related API functions
export interface ApplicationData {
  id: string;
  email: string;
  status: 'pending' | 'approved' | 'rejected';
  first_name: string;
  last_name: string;
  phone: string;
  location: string;
  availability: string;
  resume_url?: string;
  created_at: string;
  updated_at: string;
}

export interface Project {
  id: number;
  name: string;
  description: string;
  status: 'Active' | 'Completed' | 'Unassigned';
  created_at: string;
  participant_count?: number;
  is_joined?: boolean;
}




// Generic API request function
async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const config: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };
  
  // Add auth token if available
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    };
  }
  
  // Only stringify if body is an object and not FormData
  if (config.body && typeof config.body === 'object' && !(config.body instanceof FormData)) {
    config.body = JSON.stringify(config.body);
    config.headers = {
      ...config.headers,
      'Content-Type': 'application/json',
    };
  } else if (config.body instanceof FormData) {
    // Let the browser set the correct Content-Type for FormData
    if (config.headers) {
      const headers = new Headers(config.headers as HeadersInit);
      headers.delete('Content-Type');
      config.headers = headers;
    }
  }
  
  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
}

export interface ProjectJoinStatus {
  is_joined: boolean;
}

export interface ProjectParticipantCount {
  count: number;
}

// project-related API functions
export async function fetchProjects(userType: 'admin' | 'volunteer' | 'public' = 'public', volunteerId?: number): Promise<Project[]> {
  const headers: Record<string, string> = {
    'user-type': userType
  };
  
  if (volunteerId) {
    headers['user-id'] = volunteerId.toString();
  }
  
  return apiRequest<Project[]>('/api/projects', {
    headers
  });
}

export async function fetchProjectById(projectId: number): Promise<Project> {
  return apiRequest<Project>(`/api/projects/${projectId}`);
}

export async function createProject(name: string, description: string, status: string = 'Unassigned'): Promise<Project> {
  return apiRequest<Project>('/api/projects', {
    method: 'POST',
    body: JSON.stringify({ name, description, status })
  });
}

export async function updateProject(projectId: number, updates: Partial<Project>): Promise<Project> {
  return apiRequest<Project>(`/api/projects/${projectId}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  });
}

export async function deleteProject(projectId: number): Promise<ApiResponse> {
  return apiRequest<ApiResponse>(`/api/projects/${projectId}`, {
    method: 'DELETE'
  });
}

export async function joinProject(projectId: number, volunteerId: string): Promise<ApiResponse> {
  return apiRequest<ApiResponse>(`/api/projects/${projectId}/join`, {
    method: 'POST',
    body: JSON.stringify({ volunteerId })
  });
}

export async function leaveProject(projectId: number, volunteerId: string): Promise<ApiResponse> {
  return apiRequest<ApiResponse>(`/api/projects/${projectId}/leave`, {
    method: 'POST',
    body: JSON.stringify({ volunteerId })
  });
}

export async function getProjectParticipantCount(projectId: number): Promise<ProjectParticipantCount> {
  return apiRequest<ProjectParticipantCount>(`/api/projects/${projectId}/participants/count`);
}

export async function checkProjectJoinStatus(projectId: number, volunteerId: string): Promise<ProjectJoinStatus> {
  return apiRequest<ProjectJoinStatus>(`/api/projects/${projectId}/join-status?volunteerId=${volunteerId}`);
}

// Task-related functions
export async function fetchProjectTasks(projectId: number): Promise<any[]> {
  return apiRequest<any[]>(`/api/projects/${projectId}/tasks`);
}

export async function applyForTask(taskId: number, volunteerId: string): Promise<ApiResponse> {
  return apiRequest<ApiResponse>(`/api/tasks/${taskId}/apply`, {
    method: 'POST',
    body: JSON.stringify({ volunteerId })
  });
}

export async function fetchJoinedProjects(volunteerId: string): Promise<Project[]> {
  return apiRequest<Project[]>(`/api/volunteer/${volunteerId}/joined-projects`);
}

export async function fetchAppliedTasks(volunteerId: string): Promise<number[]> {
  return apiRequest<number[]>(`/api/volunteer/${volunteerId}/applied-tasks`);
}

export async function fetchVolunteerTasks(volunteerId: string): Promise<any[]> {
  return apiRequest<any[]>(`/api/volunteer/${volunteerId}/my-tasks`);
}

export async function fetchPendingAssignments(): Promise<any[]> {
  return apiRequest<any[]>(`/api/assignments/pending`);
}

export async function updateAssignmentStatus(assignmentId: number, status: string): Promise<ApiResponse> {
  return apiRequest<ApiResponse>(`/api/assignments/${assignmentId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status })
  });
}

// Email-based auth functions
export async function verifyUserRoleByEmail(email: string): Promise<UserRoleResponse> {
  return apiRequest<UserRoleResponse>(`/api/auth/user-role-by-email/${encodeURIComponent(email)}`);
}

export async function getUserRoleByEmail(email: string): Promise<UserRoleResponse> {
  return apiRequest<UserRoleResponse>(`/api/auth/user-role-by-email/${encodeURIComponent(email)}`);
}

export async function getVolunteerIdByEmail(email: string): Promise<{id: string}> {
  return apiRequest<{id: string}>(`/api/auth/volunteer-id-by-email/${encodeURIComponent(email)}`);
}

// Create user role mapping
export async function createUserRoleMapping(email: string, firebaseUid: string, isAdmin: boolean = false): Promise<ApiResponse> {
  return apiRequest<ApiResponse>('/api/auth/create-user-role', {
    method: 'POST',
    body: JSON.stringify({ email, firebaseUid, isAdmin }) // Explicitly stringify
  });
}

// Link volunteer to Firebase (now uses email)
export async function linkVolunteerToFirebase(email: string, firebaseUid: string): Promise<ApiResponse> {
  return apiRequest<ApiResponse>('/api/auth/create-user-role', {
    method: 'POST',
    body: JSON.stringify({ email, firebaseUid, isAdmin: false }) // Explicitly stringify
  });
}

// Application-related API functions
export interface ApplicationStatusResponse {
  application_status: 'pending' | 'approved' | 'rejected';
  status_updated_at?: string;
  application_id?: string;
}

export async function fetchApplicationData(applicationId: string): Promise<ApplicationData> {
  return apiRequest<ApplicationData>(`/api/applications/${applicationId}`);
}

export async function submitApplication(applicationData: FormData): Promise<{applicationId: string}> {
  return apiRequest<{applicationId: string}>('/api/applications', {
    method: 'POST',
    body: applicationData,
  });
}

export async function checkApplicationStatus(email: string): Promise<ApplicationStatusResponse> {
  return apiRequest<ApplicationStatusResponse>(`/api/application-status?email=${encodeURIComponent(email)}`);
}

export async function getApplicationStatus(applicationId: string): Promise<ApplicationStatusResponse> {
  return apiRequest<ApplicationStatusResponse>(`/api/applications/${applicationId}/status`);
}

export async function resendApplicationEmail(email: string): Promise<ApiResponse> {
  return apiRequest<ApiResponse>('/api/applications/resend-email', {
    method: 'POST',
    body: JSON.stringify({ email })
  });
}
// Firebase UID-based auth functions
export async function verifyUserRole(firebaseUid: string, requestedRole: string): Promise<ApiResponse> {
  return apiRequest<ApiResponse>('/api/auth/verify-role', {
    method: 'POST',
    body: JSON.stringify({ firebaseUid, requestedRole }) // Explicitly stringify
  });
}

export async function getUserRole(firebaseUid: string): Promise<UserRoleResponse> {
  return apiRequest<UserRoleResponse>(`/api/auth/user-role/${firebaseUid}`);
}

export async function getVolunteerIdByFirebaseUid(firebaseUid: string): Promise<{id: string}> {
  return apiRequest<{id: string}>(`/api/auth/volunteer-id/${firebaseUid}`);
}

// Utility function to set auth token
export function setAuthToken(token: string | null): void {
  if (token) {
    localStorage.setItem('authToken', token);
  } else {
    localStorage.removeItem('authToken');
  }
}

// Utility function to get auth token
export function getAuthToken(): string | null {
  return localStorage.getItem('authToken');
}